"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Facebook, Twitter, Linkedin, MapPin, Phone, Mail, Building } from "lucide-react";
function FooterQuickLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="225059" className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        {[{
        label: "Home",
        href: "/HomePage"
      }, {
        label: "Our Services",
        href: "#"
      }, {
        label: "About Clinic",
        href: "#"
      }, {
        label: "Book Appointment",
        href: "https://polmedprimarycare.com/contact-us/"
      }].map((item, i) => <li key={i} data-index={i}>
            <Link to={item.href} className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">
              {item.label}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const legalLinks = [{
    label: "Patient Privacy Policy",
    href: "#"
  }, {
    label: "Terms of Service",
    href: "#"
  }, {
    label: "Notice of Privacy Practices",
    href: "#"
  }, {
    label: "Cookie Policy",
    href: "#"
  }];
  const socialLinks = [{
    icon: Facebook,
    href: "https://www.facebook.com/p/Polmed-Primary-Care-100069100816119/",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "https://x.com/PolmedC",
    label: "Twitter"
  }, {
    icon: Linkedin,
    href: "https://www.linkedin.com/company/polmed-primary-care-walk-in-clinic/about/",
    label: "LinkedIn"
  }];
  return <section id="clinic-footer" className="dark bg-background text-foreground pt-16 pb-8 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638797/img/image_yh9s3az9.png" alt="Polmed Primary Care Walk-In Clinic" className="h-10 w-auto object-contain" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed font-default">
              Providing compassionate, high-quality primary and walk-in care to the Safety Harbor community. Your health is our priority.
            </p>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <MapPin className="size-4 shrink-0 mt-0.5 text-primary" />
                <span className="font-default">1803 Briar Creek Blvd, Safety Harbor, FL 34695</span>
              </div>
              <div className="w-full h-32 rounded-md overflow-hidden border border-border">
                <iframe src="https://maps.google.com/maps?q=1803%20Briar%20Creek%20Blvd,%20Safety%20Harbor,%20FL%2034695&t=&z=15&ie=UTF8&iwloc=&output=embed" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" title="Clinic Location"></iframe>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="size-4 shrink-0 text-primary" />
                <span className="font-default">727-216-0505</span>
              </div>
              <div className="flex items-center gap-3">
                <Building className="size-4 shrink-0 text-primary" />
                <span className="font-default">Mon–Fri 9am–5pm | Sat 10am–1pm | Sun Closed</span>
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default text-lg font-bold tracking-tight text-foreground">Navigation</h3>
            <MenuProvider menu_id="footer_menu">
              <FooterQuickLinks />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default text-lg font-bold tracking-tight text-foreground">Legal & Compliance</h3>
            <ul className="space-y-3">
              {legalLinks.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary transition-all duration-300 text-sm font-default relative group inline-flex items-center">
                    <span className="relative z-10">{link.label}</span>
                    <span className="absolute inset-0 bg-primary/10 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 scale-110"></span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default text-lg font-bold tracking-tight text-foreground">Connect With Us</h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Button key={i} data-index={i} variant="outline" size="icon" className="rounded-full border-border hover:border-primary hover:text-primary hover:bg-primary/10 transition-all duration-300" asChild>
                    <Link to={social.href} aria-label={social.label}>
                      <Icon className="size-4" />
                    </Link>
                  </Button>;
            })}
            </div>
          </div>
        </div>

        <Separator className="mb-8 opacity-20" />

        {}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p className="font-default">© {new Date().getFullYear()} Polmed Primary Care Walk-In Clinic. All rights reserved.</p>
          <p className="flex items-center gap-1 font-default font-medium">
            Excellence in Healthcare
          </p>
        </div>
      </div>
    </section>;
}